// tabs/passive-abilities.ts — the Passive Abilities table, rendered client-side from the caster
// blob, GROUPED BY GRANTING SOURCE (the provenance tree). Successor to the flat port of
// render_passive_abilities_table() (includes/functions/janus/core/list_display_helpers.php).
//
// Layout: one GROUP per source — Ancestry / Class / Subclass / Skill / equipped Inventory item /
// the character itself — a colspan header row (source art + name + type), then the granted
// passives as rows beneath it, indented per nesting depth. Provenance is derived from how the
// blob actually nests (NOT built_from flags):
//   • entry.source → the granting item (class / ancestry / subclass / skill …) or the inventory
//     ROW uuid (equipment + enchantment grants compile with inventory.inventory_id as source —
//     resolved against caster.inventory, labeled with the row's custom inventory_name and the
//     nested item's art, with an "Enchanted" chip when an enchantment template is applied).
//   • an entry whose source is ANOTHER passive nests under that passive's row
//     (Draconic Awakening → ⤷ Ancient Senses), not under a group of its own.
//   • choice links (choice_item_id / exact_choice_item_id / user_choices) nest picks under their
//     granting feat/choice passive — non-passive picks render as light provenance rows.
// A TABLE-WIDE visited set guarantees each ability renders exactly ONCE — a nested child no
// longer ALSO renders as its own top-level row (the old per-row visited double-count).
//
// Order: fixed family sections (groupRank) — Character → Ancestry (slot 1, 2) → Class/Subclass
// (slot-paired: each class followed by its subclass) → Skills → other sources → Equipment.
// Members keep the requires_level/requires_class_level ASC sort within their group. Headers are
// hue-coded per family (ft-pgb--*). The per-row source icon is replaced by a bare level badge
// (the group header now carries the source identity), and the ⤷ arrow appears only on nested
// rows (choice picks / derived grants), not direct grants. Inactive passives (conditions not
// met) stay greyed (table-warning). Search: every row carries data-ft-ability-name — a row's
// tokens include its own name, its ancestors' names, all descendant names, and the group label,
// so a match keeps the surrounding context visible.

import type { CachedCaster } from '../types';
import {
    abilityNest,
    abilityEntryId,
    abilityEntryName,
    abilityEntryCharges,
    abilityEntryMaxCharges,
    abilityEntryRechargeBegin,
    abilityEntryRequiresLevel,
    abilityRequiredLevelSort,
    abilitySourceMap,
    requiredSkillTierEntries,
    passiveActive,
    abilityEntriesOfType,
    blobSourceIndex,
    resolveAbilityNest,
    videoFreezeFrameCell,
    chargeInput,
    iconPh,
    uuidOf,
    catalogNameById,
    essenceNameById,
    sourceIdentity,
} from './ability-row';
import { itemsCol, picturesOf } from '../../shared/array-types';
// item_name is persisted HTML-ENCODED; decode before the escapeHtml/searchAttr consumers so the
// entity's & isn't re-encoded into a visible "&#039;". abilityEntryName/catalogNameById already
// decode internally — these are the paths that read items.item_name directly.
import { decodeHtmlEntitiesForPlainText } from '../../shared/decode-html-entities';
import { isCreatureSubTypeItemType } from '../../shared/item-type-badge';

// ── collapse persistence (Phase 2 item 2) — localStorage, NEVER cookies ──
// One versioned key holding { [casterId]: collapsedGroupKeys[] }. Hard-bounded: ~16 casters
// (LRU by insertion order — a write re-inserts its caster at the tail) × ~32 keys; only
// NON-default state is stored (everything-expanded casters are absent). Any parse/quota
// error nukes the key and continues — collapse is a nicety, never allowed to break a render.
// A format change bumps the version suffix (clean abandon, not a migration).
const COLLAPSE_LS_KEY = 'ft_pg_collapse_v1';
const COLLAPSE_MAX_CASTERS = 16;
const COLLAPSE_MAX_KEYS = 32;

function readCollapseStore(): Record<string, string[]> {
    try {
        if (typeof localStorage === 'undefined') return {};
        const raw = localStorage.getItem(COLLAPSE_LS_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) throw new Error('bad shape');
        return parsed as Record<string, string[]>;
    } catch {
        try { localStorage.removeItem(COLLAPSE_LS_KEY); } catch { /* nicety */ }
        return {};
    }
}

function collapsedGroupKeys(casterId: string): Set<string> {
    if (casterId === '') return new Set();
    const list = readCollapseStore()[casterId];
    return new Set(Array.isArray(list) ? list.slice(0, COLLAPSE_MAX_KEYS).map(String) : []);
}

function writeCollapsedGroupKeys(casterId: string, keys: string[]): void {
    if (casterId === '') return;
    try {
        if (typeof localStorage === 'undefined') return;
        const store = readCollapseStore();
        delete store[casterId]; // re-insert at the tail = freshest for the LRU sweep below
        if (keys.length > 0) store[casterId] = keys.slice(0, COLLAPSE_MAX_KEYS);
        const ids = Object.keys(store);
        for (let i = 0; i < ids.length - COLLAPSE_MAX_CASTERS; i++) {
            const staleId = ids[i];
            if (staleId !== undefined) delete store[staleId];
        }
        if (Object.keys(store).length === 0) localStorage.removeItem(COLLAPSE_LS_KEY);
        else localStorage.setItem(COLLAPSE_LS_KEY, JSON.stringify(store));
    } catch {
        try { localStorage.removeItem(COLLAPSE_LS_KEY); } catch { /* nicety */ }
    }
}

/** One step of a group header's provenance trail (Character → Priest Class → Faith in Life
 *  Subclass). The LAST crumb is the granting source (bold); earlier crumbs are context. */
interface Crumb {
    iconHtml: string;   // resolved art ('' = none)
    label: string;
    typeLabel?: string; // small uppercase type chip ('Ancestry', 'Class', 'Jewelry', …)
    typeIconKey?: string; // item-TYPE glyph rendered INSIDE the chip before the word — left ''
                          // when the main art already fell back to that same type icon (no dup)
    infoId?: string;    // openItemInfo target for this crumb
    muted?: boolean;    // context crumb (the 'Character' / 'Inventory' hops)
}

/** Header family — drives the band hue (ft-pgb--*) and the fixed section ordering. The three
 *  creature_* families are the taxonomy carriers (Undead creature_type, a template, …) that
 *  grant a creature's passives — surfaced via the abilitySourceMap fallback in resolveGroup. */
type GroupFamily = 'character' | 'ancestry' | 'class' | 'subclass' | 'skill' | 'inventory' | 'other'
    | 'creature_template' | 'creature_type' | 'creature_sub_type';

interface GroupInfo {
    key: string;        // dedupe key (lowercased source uuid, or '_host' / '_skills')
    label: string;      // the granting source's display name (drives search tokens)
    crumbs: Crumb[];    // provenance trail rendered with → arrows
    family: GroupFamily;
    enchanted?: boolean;
    perRowSource?: boolean; // merged-band rows keep per-row provenance (the granting skill's icon)
}

interface ChildRef { entry?: unknown; ability_id: string; item_name: string; }
function recordOf(value: unknown): Record<string, unknown> | null {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
        ? value as Record<string, unknown>
        : null;
}

/** The caster.inventory row whose inventory.inventory_id matches (case-insensitive), or null. */
function inventoryRowById(caster: CachedCaster, invId: string): unknown | null {
    const list = caster?.inventory;
    if (!Array.isArray(list)) return null;
    const k = invId.toLowerCase();
    for (const r of list) {
        const id = String(r?.inventory?.inventory_id ?? '');
        if (id !== '' && id.toLowerCase() === k) return r;
    }
    return null;
}

/** The row's ACTUAL thumbnail picture url: the pictures-bucket entry matching
 *  inventory_thumbnail_pic_id (any list — item / inventory_presentation_picture), else the first picture.
 *  '' when the row carries no pictures (caller falls back to icon art). */
function inventoryThumbUrl(invRow: unknown): string {
    const thumbId = String(ftInventoryField(invRow, 'inventory_thumbnail_pic_id') ?? '').toLowerCase();
    const pics = recordOf(invRow)?.pictures;
    let first = '';
    if (pics && typeof pics === 'object') {
        for (const k of Object.keys(pics)) {
            const list = (pics as Record<string, unknown>)[k];
            if (!Array.isArray(list)) continue;
            for (const p of list) {
                const pRecord = recordOf(p);
                const url = typeof pRecord?.picture_url === 'string' ? pRecord.picture_url : '';
                if (url === '') continue;
                if (first === '') first = url;
                if (thumbId !== '' && String(p?.picture_id ?? '').toLowerCase() === thumbId) return url;
            }
        }
    }
    if (first !== '') return first;
    // ROUND-4: a thinned instance's gallery/stamp rows live in the root-catalog template def —
    // picturesOf bridges them (pointer-id match for the stamp, def gallery for 'item').
    for (const t of ['inventory_thumbnail_picture', 'item']) {
        const bridged = picturesOf(invRow, t);
        if (bridged.length > 0) {
            const primary = bridged.find((p: unknown) => Number((p && typeof p === 'object' ? (p as Record<string, unknown>).primary_picture : 0)) === 1) ?? bridged[0];
            const url = primary?.picture_url;
            if (typeof url === 'string' && url !== '') return url;
        }
    }
    return '';
}

/** Host display name: creature blobs carry items.item_name; PC blobs the character name at
 *  player_characters.player_name (encounter rows at encounter_creature_name). */
function hostName(caster: CachedCaster): string {
    return decodeHtmlEntitiesForPlainText(
        String(ftItemsCol(caster, 'item_name') ?? '')
        || String(caster.encounter_creatures?.encounter_creature_name ?? '')
        || String(caster.player_characters?.player_name ?? '')
        || 'Character',
    );
}

/** Resolved art for a source uuid: its registered icon, else (inventory rows) the nested item's
 *  icon, else a primary-video freeze-frame, else the item-TYPE icon. `typeFallback` reports the
 *  last tier — when the main art IS already the type glyph, the crumb's type chip skips its own
 *  copy of that glyph (no side-by-side duplicate). */
function sourceArtHtml(caster: CachedCaster, src: string, label: string, size = 22): { html: string; typeFallback: boolean } {
    const reg = (typeof ICON_LOOKUP !== 'undefined') ? ICON_LOOKUP : undefined;
    if (reg && reg[src]) return { html: iconMap(src, size, size), typeFallback: false };
    const blob = blobSourceIndex(caster);
    const info = blob.types[src.toLowerCase()];
    if (info?.iconId && reg && reg[info.iconId]) return { html: iconMap(info.iconId, size, size), typeFallback: false };
    const vid = blob.videos[src.toLowerCase()]
        ?? (info?.iconId ? blob.videos[info.iconId.toLowerCase()] : undefined);
    if (vid) return { html: videoFreezeFrameCell(vid, src, label, ''), typeFallback: false };
    // Type-icon fallback resolves through the root catalog too — a thinned-out def (class /
    // ancestry / subclass living only in root.catalog) still gets its type glyph, not 'host'.
    return { html: iconMap(info?.type || sourceIdentity(caster, src).type || 'host', size, size), typeFallback: true };
}

/** For a subclass source, the OWNING class as a middle crumb (Character → Priest Class →
 *  Faith in Life Subclass) — class_N / subclass_N share a slot in player_characters. */
function classCrumbForSubclass(caster: CachedCaster, subclassId: string): Crumb | null {
    const pc = caster.player_characters;
    if (!pc || typeof pc !== 'object') return null;
    const k = subclassId.toLowerCase();
    for (let x = 1; x <= 20; x++) {
        const sub = pc['subclass_' + x];
        if (sub === undefined || sub === null || sub === '') continue;
        const sid = uuidOf(sub);
        if (!sid || sid.toLowerCase() !== k) continue;
        const cid = uuidOf(pc['class_' + x]);
        if (!cid) return null;
        const label = sourceIdentity(caster, cid).name || 'Class';
        const art = sourceArtHtml(caster, cid, label);
        return { iconHtml: art.html, label, typeLabel: 'Class', typeIconKey: art.typeFallback ? '' : 'class', infoId: cid };
    }
    return null;
}

/** Resolve an entry's source uuid into the group descriptor (provenance crumb trail).
 *  Everything spawns from the character, so headers DON'T repeat a "Character →" crumb —
 *  only the host's own group is labeled as the character. Creature passives compile with
 *  source:'NA' — for those, fall back to the provider carrier that nests the ability
 *  (abilitySourceMap: creature_template / creature_type / creature_sub_type /
 *  classifications) so e.g. Undead-granted passives band under "Undead — Creature Type"
 *  instead of all flattening into the host band; then to the gating requires_skill_tier_N
 *  skill (the Lich's Advanced Armor Training → Natural Armor Skill) — the skill grants the
 *  passive, mirroring sourceIconCell's skill-gate fallback. */
function resolveGroup(caster: CachedCaster, source: unknown, entryId = '', entry?: unknown): GroupInfo {
    let src = (source === null || source === undefined || source === '' || source === 'NA') ? '' : String(source);
    // Synthetic non-UUID sources ('host' on the auto-granted Short Rest et al.) are the character.
    if (src !== '' && typeof checkIfValidUUID === 'function' && !checkIfValidUUID(src)) src = '';
    if (src === '' && entryId !== '') {
        const carrier = abilitySourceMap(caster)[entryId.toLowerCase()];
        if (carrier) src = carrier;
    }
    if (src === '' && entry) {
        for (const { entry: sk } of requiredSkillTierEntries(resolveAbilityNest(entry, caster))) {
            const u = uuidOf(sk);
            if (u) { src = u; break; }
        }
    }
    if (src === '') {
        const hn = hostName(caster);
        return {
            key: '_host',
            label: hn,
            family: 'character',
            crumbs: [{ iconHtml: iconMap('host', 22, 22), label: hn, typeLabel: 'Character' }],
        };
    }

    // Inventory ROW source (equipment / enchantment grants): Inventory → the row, labeled with
    // its custom inventory_name and the nested item's art.
    const invRow = inventoryRowById(caster, src);
    if (invRow) {
        // itemsCol bridges the ROUND-6 #1 pointer-bucket drop (template id rides
        // inventory.inventory_item_id; name/type resolve via the root catalog).
        const itemId = String(itemsCol(invRow, 'item_id') ?? '');
        const itemType = String(itemsCol(invRow, 'item_type') ?? '') || 'inventory';
        const label = decodeHtmlEntitiesForPlainText(
            String(ftInventoryField(invRow, 'inventory_name') ?? '')
            || String(itemsCol(invRow, 'item_name') ?? '')
            || 'Inventory Item',
        );
        const enchanted = !!(recordOf(recordOf(invRow)?.computed)?.enchantment
            || String(ftInventoryField(invRow, 'enchantment_template_id') ?? '') !== '');
        // Art: the row's ACTUAL thumbnail picture (what the inventory tiles show), falling back to
        // the nested item's registry icon, then its type icon. The crumb's openItemInfo(inventory_id)
        // click renders instantly via the object-card cache path (ftRenderItemFromCache resolves
        // inventory nodes straight from the cached caster blob — no server round-trip).
        const reg = (typeof ICON_LOOKUP !== 'undefined') ? ICON_LOOKUP : undefined;
        const thumbUrl = inventoryThumbUrl(invRow);
        const artIsTypeIcon = thumbUrl === '' && !(itemId !== '' && reg && reg[itemId]);
        const art = thumbUrl !== ''
            ? `<img src="${escapeHtml(thumbUrl)}" alt="" loading="lazy" class="ft-crumb-thumb">`
            : ((itemId !== '' && reg && reg[itemId]) ? iconMap(itemId, 22, 22) : iconMap(itemType, 22, 22));
        return {
            key: src.toLowerCase(),
            label,
            family: 'inventory',
            crumbs: [
                { iconHtml: iconMap('inventory', 22, 22), label: 'Inventory', muted: true },
                { iconHtml: art, label, typeLabel: ucwordsAndSpace(itemType), typeIconKey: artIsTypeIcon ? '' : itemType, infoId: src },
            ],
            enchanted,
        };
    }

    // Status-granted ability: the grant compiles with source = the live status_id (random-table /
    // contingent grants like Daily Spin: Coilskin, imbues, potion grants, …). Those don't resolve
    // to a class/ancestry/inventory source, so they used to fall through to an unlabeled "Source"
    // band. Group them ALL under one "Status Effects" section — the ability is coming FROM a status
    // effect (mirrors where the effect itself shows on the sheet).
    if (typeof ftCreatureStatuses === 'function') {
        let fromStatus = false;
        try {
            const sts = ftCreatureStatuses(caster, 'target') || [];
            fromStatus = Array.isArray(sts)
                && sts.some((st: unknown) => String(st && typeof st === 'object' ? (st as Record<string, unknown>).status_id ?? '' : '').toLowerCase() === src.toLowerCase());
        } catch { fromStatus = false; }
        if (fromStatus) {
            return {
                key: '_status_effects',
                label: 'Status Effects',
                family: 'other',
                crumbs: [{ iconHtml: iconMap('status_effect', 22, 22), label: 'Status Effects', typeLabel: '' }],
            };
        }
    }

    // sourceIdentity, not the raw blob index: catalogized defs (class / ancestry / subclass)
    // were thinned OUT of the caster blob and live only in root.catalog — without the fallback
    // every such group header degraded to the literal 'Source' with family 'other'.
    const ident = sourceIdentity(caster, src);
    const srcType = ident.type;
    const label = ident.name || srcType || 'Source';
    const typeLabel = srcType ? ucwordsAndSpace(srcType) : '';
    const family: GroupFamily =
        srcType === 'ancestry' ? 'ancestry'
        : srcType === 'class' ? 'class'
        : srcType === 'subclass' ? 'subclass'
        : srcType === 'creature_template' ? 'creature_template'
        : srcType === 'creature_type' ? 'creature_type'
        // The whole creature_sub_type item_type FAMILY (2026-07-15 division: ancestry / grouping / lineage, all stored
        // in the `creature_sub_type` layer) — a Lich lineage or a Black Dragons ancestry grouped as 'other' before.
        : isCreatureSubTypeItemType(srcType) ? 'creature_sub_type'
        : srcType.includes('skill') ? 'skill'
        : 'other';
    const crumbs: Crumb[] = [];
    if (family === 'subclass') {
        const cc = classCrumbForSubclass(caster, src);
        if (cc) crumbs.push(cc);
    }
    const art = sourceArtHtml(caster, src, label);
    crumbs.push({ iconHtml: art.html, label, typeLabel, typeIconKey: (art.typeFallback || srcType === '') ? '' : srcType, infoId: src });
    return { key: src.toLowerCase(), label, family, crumbs };
}

/** Fixed section order: Character → Ancestry (slot 1, 2) → Class/Subclass (slot-paired, the
 *  subclass right after its class) → Skills → other sources → Equipment. Slot positions come
 *  from player_characters.ancestry_N / class_N / subclass_N; unmatched members of a family
 *  sort to that family's tail. */
function groupRank(caster: CachedCaster, g: GroupInfo): number {
    const pc = caster.player_characters ?? {};
    const slotOf = (prefix: string, max: number): number => {
        for (let x = 1; x <= max; x++) {
            const v = pc[prefix + x];
            if (v === undefined || v === null || v === '') continue;
            const id = uuidOf(v);
            if (id && id.toLowerCase() === g.key) return x;
        }
        return -1;
    };
    switch (g.family) {
        case 'character': return 0;
        // Creature taxonomy carriers sit right after the host band (template → type → sub_type).
        case 'creature_template': return 30;
        case 'creature_type': return 32;
        case 'creature_sub_type': return 34;
        case 'ancestry': { const s = slotOf('ancestry_', 3); return s > 0 ? 10 + s : 19; }
        case 'class': { const s = slotOf('class_', 20); return s > 0 ? 100 + s * 10 : 390; }
        case 'subclass': { const s = slotOf('subclass_', 20); return s > 0 ? 100 + s * 10 + 5 : 395; }
        case 'skill': return 500;
        case 'inventory': return 700;
        default: return 600;
    }
}

/** Children of an ability, discovered from the blob's own links: ability entries (any bucket)
 *  whose choice/source link points at it, plus user_choices picks. allByType is the memoized
 *  abilityEntriesOfType() result per bucket (one walk per render, not per row). */
function childrenOf(caster: CachedCaster, abilityId: string, allByType: Record<string, unknown[]>): ChildRef[] {
    const out: ChildRef[] = [];
    const seen = new Set<string>();
    const target = abilityId.toLowerCase();

    for (const type of Object.keys(allByType)) {
        for (const sub of allByType[type] ?? []) {
            const subRecord = recordOf(sub);
            if (!subRecord) continue;
            const link = subRecord.choice_item_id ? 'choice_item_id'
                : subRecord.exact_choice_item_id ? 'exact_choice_item_id'
                : subRecord.source ? 'source' : '';
            if (!link || String(subRecord[link]).toLowerCase() !== target) continue;
            const cid = abilityEntryId(sub);
            if (!cid || cid.toLowerCase() === target || seen.has(cid.toLowerCase())) continue;
            seen.add(cid.toLowerCase());
            out.push({ entry: sub, ability_id: cid, item_name: abilityEntryName(sub) });
        }
    }

    const ucRows = caster.user_choices;
    if (Array.isArray(ucRows)) {
        for (const uc of ucRows) {
            if (!uc || typeof uc !== 'object') continue;
            const choiceUuid = String(uc.choice ?? '');
            if (choiceUuid === '' || seen.has(choiceUuid.toLowerCase())) continue;
            let isChild = false;
            for (const col of ['exact_choice_item_id', 'choice_item_id']) {
                const p = String(uc[col] ?? '');
                if (p !== '' && p.toLowerCase() === target) { isChild = true; break; }
            }
            if (!isChild) continue;
            seen.add(choiceUuid.toLowerCase());
            // v33 (ROUND-12 #4): collapsed/elided choice_shell carries no name — resolve
            // catalog-first (this also UN-BREAKS the raw-uuid display the v21/v25 collapse
            // left behind: a bare-string shell never had items.item_name). Still-array
            // shells (non-strippable targets) keep their embedded identity.
            // essenceNameById covers essence picks (essence_feat / _ability / …): the `choice` is
            // an essence UUID that isn't in root.catalog, so catalogNameById misses it and the row
            // would otherwise print the bare UUID (e.g. Beyond Measure → Body).
            const shellName = decodeHtmlEntitiesForPlainText(String(uc.choice_shell?.items?.item_name ?? ''));
            out.push({ ability_id: choiceUuid, item_name: shellName || catalogNameById(choiceUuid) || essenceNameById(choiceUuid) || choiceUuid });
        }
    }

    // Nested CONTAINMENT grants (front-end trace, NO data-shape change): a node's OWN
    // item_layer_types.passive_ability is a feat/passive that GRANTS another passive by nesting it
    // (Spell Sniper ⤷ Far Bender), NOT a source/choice link — and the compile leaves it nested rather
    // than folding it into the passive_ability bucket (confirmed on staging). So resolve this node's
    // def from any ability bucket and add its nested passive_ability grants as children, so the tree
    // shows the full chain. childrenOf recurses, so a grant that itself nests passives keeps tracing.
    let nodeNest: unknown = null;
    for (const type of Object.keys(allByType)) {
        for (const e of allByType[type] ?? []) {
            if (String(abilityEntryId(e) ?? '').toLowerCase() === target) { nodeNest = resolveAbilityNest(e, caster); break; }
        }
        if (nodeNest) break;
    }
    if (nodeNest && typeof nodeNest === 'object' && ftLayerHas(nodeNest, 'passive_ability')) {
        for (const g of ftLayerEntries(nodeNest, 'passive_ability')) {
            const gid = (g && typeof g === 'object') ? (uuidOf(g) || String(abilityEntryId(g) ?? '')) : String(g ?? '');
            if (!gid || gid.toLowerCase() === target || seen.has(gid.toLowerCase())) continue;
            seen.add(gid.toLowerCase());
            const gname = ((g && typeof g === 'object') ? abilityEntryName(g) : '') || catalogNameById(gid) || gid;
            out.push({ entry: (g && typeof g === 'object') ? g : undefined, ability_id: gid, item_name: gname });
        }
    }
    return out;
}

/** Small muted "choice came from" notes below the name (exact_choice_item_id / choice_item_id). */
function choiceNotes(entry: unknown, abilityId: string): string {
    const ids: string[] = [];
    for (const col of ['exact_choice_item_id', 'choice_item_id']) {
        const cid = String(recordOf(entry)?.[col] ?? '');
        if (cid !== '' && cid !== abilityId && !ids.includes(cid)) ids.push(cid);
    }
    if (ids.length === 0) return '';
    const parts = ids.map((nid) =>
        `<span role="button" class="link-secondary" onclick="openItemInfo('${escapeHtml(nid)}');" data-ft-info-item-id="${escapeHtml(nid)}">${iconPh(nid, 18, 18, false)}</span>`
    );
    return `<div class="small text-muted mt-1 ps-1">${parts.join('<span class="text-muted"> · </span>')}</div>`;
}

// ── inactive "why" (Phase 2 item 6, v1) ──
// The condition layers live ON the ability nest; NAMING them is enough for the player to act
// ("Requires: Shield, Wooden equipped"). v1 does NOT evaluate which clause failed — the family
// list mirrors check_ability_conditions() (includes/functions/janus/core/check_ability_conditions.php).
const CONDITION_FAMILIES: { layer: string; label: (name: string) => string }[] = [
    { layer: 'if_armor_type_equipped_active', label: (n) => `${n} equipped` },
    { layer: 'if_armor_type_equipped_not_active', label: (n) => `${n} not equipped` },
    { layer: 'if_weapon_classification_equipped_active', label: (n) => `${n} equipped` },
    { layer: 'if_weapon_classification_equipped_not_active', label: (n) => `${n} not equipped` },
    { layer: 'if_weapon_type_equipped_active', label: (n) => `${n} equipped` },
    { layer: 'if_weapon_type_equipped_not_active', label: (n) => `${n} not equipped` },
    { layer: 'if_weapon_has_property_equipped_active', label: (n) => `weapon with ${n} equipped` },
    { layer: 'if_weapon_has_property_equipped_not_active', label: (n) => `no weapon with ${n} equipped` },
    { layer: 'if_caster_has_status_effect_active', label: (n) => `${n} active` },
    { layer: 'if_caster_has_status_effect_not_active', label: (n) => `${n} not active` },
    { layer: 'if_light_type_active', label: (n) => `light: ${n}` },
    // Set-enchantment precondition — ALL listed items must be equipped (plans/set-enchantment-preconditions.md)
    { layer: 'requires_equipped_item', label: (n) => `${n} equipped` },
];

/** A condition layer entry's display name: nested item's item_name, else its uuid resolved
 *  through the blob index, else (non-uuid scalars like the weapon-property key
 *  'weapon_two_handed') the prettified raw value. '' when unresolvable. */
function conditionEntryName(caster: CachedCaster, v: unknown): string {
    if (v && typeof v === 'object') {
        const nm = ftItemsCol(v, 'item_name');
        if (typeof nm === 'string' && nm !== '') return decodeHtmlEntitiesForPlainText(nm);
        const id = uuidOf(v);
        if (id !== '') return sourceIdentity(caster, id).name;
        return '';
    }
    const s = String(v ?? '');
    if (s === '') return '';
    if (typeof checkIfValidUUID === 'function' && checkIfValidUUID(s)) {
        return sourceIdentity(caster, s).name;
    }
    return ucwordsAndSpace(s);
}

/** Named requirements gating this passive, for the inactive-row tooltip + sub-line.
 *  Enumerates the if_* condition families, requires_skill_tier_1..6, and the
 *  charge-at-median link — the same set check_ability_conditions() walks. */
function inactiveRequirements(caster: CachedCaster, entry: unknown): string[] {
    const nest = resolveAbilityNest(entry, caster);
    const out: string[] = [];
    for (const fam of CONDITION_FAMILIES) {
        if (!ftLayerHas(nest, fam.layer)) continue;
        for (const v of ftLayerEntries(nest, fam.layer)) {
            const n = conditionEntryName(caster, v);
            if (n !== '') out.push(fam.label(n));
        }
    }
    for (const { tier, entry: sk } of requiredSkillTierEntries(nest)) {
        const n = conditionEntryName(caster, sk);
        if (n !== '') out.push(`${n} tier ${tier}`);
    }
    const med = ftLayerValue(nest, 'ability_active_if_ability_charge_is_at_median');
    if (med !== null && med !== undefined && med !== '') {
        const n = conditionEntryName(caster, med);
        out.push(`${n !== '' ? n : 'linked ability'} charges at median`);
    }
    return Array.from(new Set(out));
}

/** Per-row provenance icon for rows inside the merged Skills band (item 1): the granting
 *  skill's art, clickable, sitting beside the level badge — so folding the per-skill bands
 *  doesn't lose which skill granted each passive. '' when the source isn't a resolvable uuid. */
function rowSourceIcon(caster: CachedCaster, entry: unknown): string {
    let src = String(recordOf(entry)?.source ?? '');
    if (src === '' || src === 'NA') {
        src = '';
        for (const { entry: skill } of requiredSkillTierEntries(resolveAbilityNest(entry, caster))) {
            const id = uuidOf(skill);
            if (id !== '') { src = id; break; }
        }
    }
    if (src === '') return '';
    if (typeof checkIfValidUUID === 'function' && !checkIfValidUUID(src)) return '';
    const label = sourceIdentity(caster, src).name || 'Source';
    return `<span role="button" class="ft-row-src-icon" onclick="openItemInfo('${escapeHtml(src)}');"`
        + ` data-ft-info-item-id="${escapeHtml(src)}" title="${escapeHtml(label)}">${sourceArtHtml(caster, src, label, 20).html}</span>`;
}

/** Bare level badge for the (former source-icon) column: requires_class_level wins over
 *  requires_level — the group header now carries the source identity, so only the gate shows. */
function rowLevelBadge(entry: unknown): string {
    let lvl: unknown = ftLayerValue(abilityNest(entry), 'requires_class_level');
    if (lvl === null || lvl === undefined || lvl === '') lvl = abilityEntryRequiresLevel(entry);
    if (lvl === null || lvl === undefined || lvl === '') return '';
    return `<span class="badge bg-dark border text-white" title="Requires level ${escapeHtml(String(lvl))}">${escapeHtml(String(lvl))}</span>`;
}

function searchAttr(tokens: string[]): string {
    return escapeHtml(Array.from(new Set(tokens.map((t) => String(t ?? '').toLowerCase().trim()).filter(Boolean))).join(' '));
}

interface RenderCtx {
    caster: CachedCaster;
    canEdit: boolean;
    byId: Record<string, unknown>;      // compiled passive entries by lowercased ability id
    allByType: Record<string, unknown[]>;   // memoized abilityEntriesOfType per bucket
    visited: Set<string>;               // table-wide: each ability renders ONCE
    groupLabel: string;
    namePool: string[];                 // collects every rendered name for the group header search attr
    groupKey: string;                   // collapse: member rows carry data-ft-pg-of=<key>
    groupCollapsed: boolean;            // collapse: rows render pre-collapsed (survives WS repaints)
    showCharges: boolean;               // item 3: the charges column exists only when some row uses it
    perRowSourceIcon: boolean;          // item 1: merged Skills band keeps per-row provenance
}

const INDENT_REM = 1.4;

/** The name cell body. Depth 0 (a direct grant from the header's source) renders flush — the
 *  header already states the provenance. The ⤷ arrow appears only on NESTED rows (depth ≥ 1:
 *  choice picks, passive-granted passives), where it actually means "derived from the row
 *  above" — arrows on every row read as noise ("side-arrow hell"). */
function treeNameDiv(depth: number, inner: string): string {
    if (depth <= 0) return `<div class="ft-passive-tree-name">${inner}</div>`;
    return `<div class="ft-passive-tree-name" style="padding-left:${depth * INDENT_REM}rem;">`
        + `<span class="ft-tree-arrow" aria-hidden="true">⤷</span>${inner}</div>`;
}

/** One full passive row (charges, inactive greying) + its child rows, depth-indented.
 *  Returns { html, names } — names = self + all descendants, folded into ancestors' search attrs. */
function renderEntryRows(ctx: RenderCtx, entry: unknown, depth: number, ancestorNames: string[]): { html: string; names: string[] } {
    const abilityId = abilityEntryId(entry);
    if (!abilityId) return { html: '', names: [] };
    const k = abilityId.toLowerCase();
    if (ctx.visited.has(k)) return { html: '', names: [] };
    ctx.visited.add(k);

    const name = abilityEntryName(entry);
    ctx.namePool.push(name);

    // Children first (bottom-up), so this row's search attr can include descendant names.
    let childHtml = '';
    const descendantNames: string[] = [];
    for (const child of childrenOf(ctx.caster, abilityId, ctx.allByType)) {
        const full = ctx.byId[child.ability_id.toLowerCase()];
        const sub = full
            ? renderEntryRows(ctx, full, depth + 1, [...ancestorNames, name])
            : renderLightRows(ctx, child, depth + 1, [...ancestorNames, name]);
        childHtml += sub.html;
        descendantNames.push(...sub.names);
    }

    const inactive = !passiveActive(entry);
    const ro = !ctx.canEdit;
    // Name is ALWAYS clickable — the info popup is read-only viewing (buildRow does the same);
    // canEdit gates only the charge inputs. Static statblocks (Lich item page) had dead names.
    const opener = `<span role="button" onclick="openItemInfo('${escapeHtml(abilityId)}');">${escapeHtml(name)}</span>`;

    // Inactive rows SAY WHY (v1): name the gating condition layers in a muted sub-line +
    // the tooltip. No client evaluation of which clause failed — listing the requirements
    // is already the info the player needs. Active rows stay clean.
    let requiresLine = '';
    let trTitle = '';
    if (inactive) {
        const reqs = inactiveRequirements(ctx.caster, entry);
        if (reqs.length > 0) {
            const joined = reqs.join(' · ');
            const indent = depth > 0 ? ` style="padding-left:${depth * INDENT_REM}rem;"` : '';
            // No text-muted here: the line only exists on table-warning (LIGHT) rows, where the
            // muted grey vanishes — .ft-passive-requires inherits the row's dark text instead.
            requiresLine = `<div class="small ft-passive-requires"${indent}><span class="fw-semibold">Requires:</span> ${escapeHtml(joined)}</div>`;
            trTitle = ` title="Conditions not met — requires: ${escapeHtml(joined)}"`;
        } else {
            trTitle = ' title="Passive conditions not currently met"';
        }
    }
    const nameCell = treeNameDiv(depth, opener) + requiresLine + choiceNotes(entry, abilityId);

    let chargesCell = '';
    const charges = abilityEntryCharges(entry);
    if (charges !== 'NA') {
        const maxC = abilityEntryMaxCharges(entry);
        const rb = abilityEntryRechargeBegin(entry);
        const rechargeBadge = rb > 0
            ? `<h5 class="mb-0"><span class='badge bg-small bg-dark border'>${escapeHtml(String(rb))}</span> ${iconPh('recharge_range_begin')}</h5>`
            : `<h5 class="mb-0"><span class='badge bg-small bg-dark border'>${escapeHtml(String(maxC))}</span> ${iconPh('ability_resets_long_rest')}</h5>`;
        chargesCell = `<div class="d-flex align-items-center justify-content-end gap-2 ft-sheet-charges-row">`
            + chargeInput(`${abilityId}_charges`, 0, maxC, charges, ro, `changeCharges('${abilityId}','${abilityId}_charges');`)
            + `<div class="flex-shrink-0">${rechargeBadge}</div></div>`;
    }

    // Merged-Skills rows (item 1): the granting skill's icon returns beside the level badge —
    // direct grants only; nested children already show their derivation via the ⤷ arrow.
    let sourceCell = rowLevelBadge(entry);
    if (ctx.perRowSourceIcon && depth === 0) {
        const icon = rowSourceIcon(ctx.caster, entry);
        if (icon !== '') sourceCell = `<span class="d-inline-flex align-items-center gap-1">${icon}${sourceCell}</span>`;
    }

    const trClass = `fate-tide-sheet-ability-row${inactive ? ' table-warning' : ''}${ctx.groupCollapsed ? ' ft-pg-collapsed' : ''}`;
    const search = searchAttr([name, ...descendantNames, ...ancestorNames, ctx.groupLabel]);
    const html = `<tr class="${trClass}" data-ft-ability-name="${search}" data-ft-pg-of="${escapeHtml(ctx.groupKey)}"${trTitle}>
        <td class="ft-ability-cell ft-ability-cell--name align-middle">${nameCell}</td>
        <td class="ft-ability-cell ft-ability-cell--source align-middle text-center" style="width:clamp(50px,7vw,72px);">${sourceCell}</td>
        ${ctx.showCharges ? `<td class="ft-ability-cell ft-ability-cell--charges align-middle text-end ft-sheet-charges-cell">${chargesCell}</td>` : ''}
    </tr>` + childHtml;
    return { html, names: [name, ...descendantNames] };
}

/** A light provenance row — a child that is NOT a compiled passive entry (a non-passive grant or
 *  a user_choices pick): name only, indented, clickable; no charges/conditions of its own here. */
function renderLightRows(ctx: RenderCtx, ref: ChildRef, depth: number, ancestorNames: string[]): { html: string; names: string[] } {
    const k = ref.ability_id.toLowerCase();
    if (ctx.visited.has(k)) return { html: '', names: [] };
    ctx.visited.add(k);
    ctx.namePool.push(ref.item_name);

    let childHtml = '';
    const descendantNames: string[] = [];
    for (const child of childrenOf(ctx.caster, ref.ability_id, ctx.allByType)) {
        const full = ctx.byId[child.ability_id.toLowerCase()];
        const sub = full
            ? renderEntryRows(ctx, full, depth + 1, [...ancestorNames, ref.item_name])
            : renderLightRows(ctx, child, depth + 1, [...ancestorNames, ref.item_name]);
        childHtml += sub.html;
        descendantNames.push(...sub.names);
    }

    // Always clickable — see renderEntryRows; the info popup is viewing, not editing.
    const opener = `<span role="button" onclick="openItemInfo('${escapeHtml(ref.ability_id)}');">${escapeHtml(ref.item_name)}</span>`;
    const search = searchAttr([ref.item_name, ...descendantNames, ...ancestorNames, ctx.groupLabel]);
    const html = `<tr class="fate-tide-sheet-ability-row${ctx.groupCollapsed ? ' ft-pg-collapsed' : ''}" data-ft-ability-name="${search}" data-ft-pg-of="${escapeHtml(ctx.groupKey)}">
        <td class="ft-ability-cell ft-ability-cell--name align-middle">${treeNameDiv(depth, opener)}</td>
        <td class="ft-ability-cell ft-ability-cell--source align-middle text-center" style="width:clamp(50px,7vw,72px);"></td>
        ${ctx.showCharges ? '<td class="ft-ability-cell ft-ability-cell--charges align-middle text-end ft-sheet-charges-cell"></td>' : ''}
    </tr>` + childHtml;
    return { html, names: [ref.item_name, ...descendantNames] };
}

/** One provenance crumb: [art] Label [⌁ TYPE] — the last crumb (the granting source) is bold,
 *  context crumbs (Character / Inventory hops) are muted; uuid-backed crumbs are clickable.
 *  The type chip leads with the item-TYPE's own registry glyph (typeIconKey) — set '' by the
 *  builders when the main art already fell back to that same type icon. */
function crumbHtml(c: Crumb, isLast: boolean): string {
    const open = c.infoId
        ? ` role="button" onclick="openItemInfo('${escapeHtml(c.infoId)}');" data-ft-info-item-id="${escapeHtml(c.infoId)}"`
        : '';
    const label = isLast
        ? `<span class="fw-bold">${escapeHtml(c.label)}</span>`
        : escapeHtml(c.label);
    const typeIcon = c.typeIconKey ? iconMap(c.typeIconKey, 14, 14) : '';
    const type = c.typeLabel ? `<span class="ft-crumb-type">${typeIcon}${escapeHtml(c.typeLabel)}</span>` : '';
    return `<span class="ft-crumb${c.muted ? ' ft-crumb--muted' : ''}"${open}>${c.iconHtml}${label}${type}</span>`;
}

/** The colspan group header row: the provenance trail rendered as an ft-ability-hdr band,
 *  hue-coded per family (ft-pgb--ancestry / --class / --subclass / --skill / --inventory /
 *  --character / --creature_*), with → arrows between crumbs (+ Enchanted chip). Leads with
 *  the collapse chevron (data-ft-pg-toggle — handled by the document-level delegate installed
 *  in installPassiveTreeInteractions) and trails with the member-count chip. Carries every
 *  member/descendant name in data-ft-ability-name so a member match keeps the header visible. */
function groupHeaderRow(info: GroupInfo, namePool: string[], collapsed: boolean, colspan: number): string {
    const trail = info.crumbs
        .map((c, i) => crumbHtml(c, i === info.crumbs.length - 1))
        .join(`<span class="ft-crumb-arrow" aria-hidden="true">→</span>`);
    const enchChip = info.enchanted
        ? `<span class="badge bg-info text-dark border">Enchanted</span>`
        : '';
    const key = escapeHtml(info.key);
    const toggle = `<button type="button" class="ft-pg-toggle${collapsed ? ' ft-pg-toggle--collapsed' : ''}"`
        + ` data-ft-pg-toggle="${key}" aria-expanded="${collapsed ? 'false' : 'true'}"`
        + ` title="${collapsed ? 'Expand' : 'Collapse'} ${escapeHtml(info.label)}">`
        + `<span class="ft-pg-chevron" aria-hidden="true">▾</span></button>`;
    const countChip = `<span class="ft-pg-count badge bg-dark border" title="${namePool.length} passives">${namePool.length}</span>`;
    const tokens = [...info.crumbs.map((c) => c.label), ...namePool];
    return `<tr class="fate-tide-sheet-ability-row ft-passive-group-row" data-ft-ability-name="${searchAttr(tokens)}" data-ft-pg-key="${key}">
        <td colspan="${colspan}" class="ft-ability-cell ft-passive-group-cell align-middle">
            <div class="ft-ability-hdr ft-passive-group-band ft-pgb--${info.family}">${toggle}${trail}${enchChip}${countChip}</div>
        </td>
    </tr>`;
}

/** Render the Passive Abilities table (source-grouped provenance tree) from the caster blob. */
export function renderPassiveAbilities(caster: CachedCaster, casterId: string, isStatic = false): string {
    // isStatic (item-creature statblock): force read-only — passives' charge controls are inert here.
    const canEdit = !isStatic && typeof ftHasPlayerPermission === 'function' && ftHasPlayerPermission(caster);
    const entries = abilityEntriesOfType(caster, 'passive_ability');
    if (!entries || entries.length === 0) return '';

    // Item 3: pay for the charges column only when some passive actually carries charges
    // (typically 3 of ~50 rows) — otherwise the table is 2-column and the name cell gets the room.
    const hasCharges = entries.some((e) => abilityEntryCharges(e) !== 'NA');

    // Item 2: groups this caster collapsed (localStorage, bounded). Rows render pre-collapsed
    // so the state survives the innerHTML repaint on every WS caster_update.
    const collapsed = collapsedGroupKeys(casterId);

    // Level-ASC sort (stable, like buildSection) — drives member order AND group order below.
    const sorted = [...entries].sort((a, b) => abilityRequiredLevelSort(a) - abilityRequiredLevelSort(b));

    const byId: Record<string, unknown> = {};
    for (const e of sorted) {
        const id = abilityEntryId(e);
        if (id && !byId[id.toLowerCase()]) byId[id.toLowerCase()] = e;
    }

    // Memoize the per-bucket entry lists once — childrenOf scans them per row.
    const allByType: Record<string, unknown[]> = {};
    const buckets = caster.computed?.creature?.abilities;
    if (buckets && typeof buckets === 'object') {
        for (const type of Object.keys(buckets)) allByType[type] = abilityEntriesOfType(caster, type);
    }

    // A passive whose source is ANOTHER passive in the list nests under it (Draconic Awakening
    // → Ancient Senses) instead of rooting a group.
    const childIds = new Set<string>();
    for (const e of sorted) {
        const id = abilityEntryId(e);
        if (!id) continue;
        const source = e !== null && typeof e === 'object' ? (e as Record<string, unknown>).source : undefined;
        const s = String(source ?? '').toLowerCase();
        if (s !== '' && s !== id.toLowerCase() && byId[s]) childIds.add(id.toLowerCase());
    }

    // Group the root entries by resolved source (members keep the level-ASC order), then put
    // the GROUPS in the fixed section order: Character → Ancestry 1, 2 → Class 1 + its
    // subclass(es) → Class 2 + subclass(es) → Skills → other → Equipment. Array.sort is
    // stable, so same-rank groups keep their first-appearance (level-ASC) order.
    const groups: { info: GroupInfo; members: unknown[] }[] = [];
    const groupIdx: Record<string, number> = {};
    const addToGroup = (e: unknown): void => {
        const info = resolveGroup(caster, recordOf(e)?.source, abilityEntryId(e), e);
        if (groupIdx[info.key] === undefined) {
            groupIdx[info.key] = groups.length;
            groups.push({ info, members: [] });
        }
        groups[groupIdx[info.key] ?? -1]?.members.push(e);
    };
    for (const e of sorted) {
        const id = abilityEntryId(e);
        if (!id || childIds.has(id.toLowerCase())) continue;
        addToGroup(e);
    }
    groups.sort((a, b) => groupRank(caster, a.info) - groupRank(caster, b.info));

    // Item 1: single-member skill bands are noise — fold ALL skill groups into ONE synthetic
    // "Skills" band (members concatenated in group order, each group already level-ASC), with
    // per-row provenance restored via the granting skill's icon (perRowSource). Only fires at
    // ≥2 skill groups — a lone skill group keeps its named band. Skill groups share rank 500,
    // so after the sort they are contiguous: splice the merged band into the first one's slot.
    const skillGroups = groups.filter((g) => g.info.family === 'skill');
    if (skillGroups.length >= 2) {
        const mergedGroup = {
            info: {
                key: '_skills',
                label: 'Skills',
                family: 'skill' as GroupFamily,
                crumbs: [{ iconHtml: iconMap('skill', 22, 22), label: 'Skills' }],
                perRowSource: true,
            },
            members: skillGroups.flatMap((g) => g.members),
        };
        groups.splice(groups.findIndex((g) => g.info.family === 'skill'), skillGroups.length, mergedGroup);
    }

    const visited = new Set<string>();
    const sections: string[] = [];
    const renderGroup = (info: GroupInfo, members: unknown[]): void => {
        const ctx: RenderCtx = {
            caster, canEdit, byId, allByType, visited, groupLabel: info.label, namePool: [],
            groupKey: info.key, groupCollapsed: collapsed.has(info.key),
            showCharges: hasCharges, perRowSourceIcon: !!info.perRowSource,
        };
        let body = '';
        for (const m of members) body += renderEntryRows(ctx, m, 0, []).html;
        if (body !== '') sections.push(groupHeaderRow(info, ctx.namePool, ctx.groupCollapsed, hasCharges ? 3 : 2) + body);
    };
    for (const g of groups) renderGroup(g.info, g.members);

    // Defensive sweep: a source CYCLE among passives (A→B→A) would put every node in childIds and
    // strand the lot — render any stragglers under their own resolved source so nothing is lost.
    const leftovers = sorted.filter((e) => {
        const id = abilityEntryId(e);
        return id !== '' && !visited.has(id.toLowerCase());
    });
    if (leftovers.length > 0) {
        const lgGroups: { info: GroupInfo; members: unknown[] }[] = [];
        const lgIdx: Record<string, number> = {};
        for (const e of leftovers) {
            const source = e !== null && typeof e === 'object' ? (e as Record<string, unknown>).source : undefined;
            const info = resolveGroup(caster, source, abilityEntryId(e), e);
            if (lgIdx[info.key] === undefined) { lgIdx[info.key] = lgGroups.length; lgGroups.push({ info, members: [] }); }
            lgGroups[lgIdx[info.key] ?? -1]?.members.push(e);
        }
        for (const g of lgGroups) renderGroup(g.info, g.members);
    }

    if (sections.length === 0) return '';
    // Charges column (when present) narrowed 175 → 140px: the compact charge input (≤72px) +
    // recharge chip + 0.4rem gap fit comfortably. data-ft-pg-caster feeds the collapse delegate.
    const cols = `<colgroup><col><col style="width:clamp(50px,7vw,72px);">${hasCharges ? '<col style="width:140px;">' : ''}</colgroup>`;
    return `<div class="ft-pc-abilities ft-pc-passive-abilities" data-ft-pg-caster="${escapeHtml(casterId)}">
        <div class="fate-tide-sheet-ability-section">
            <h5 class="text-start ps-3 mt-1 mb-0 pt-2 pb-2 bg-primary text-dark bg-gradient rounded-top"><span class="d-inline-flex align-items-center gap-2">${iconPh('passive_ability', 22, 22, false)}Passive Abilities</span></h5>
            <div class="table-responsive-xxl mb-3">
                <table class="table table-hover table-sm ft-passive-tree-table" style="table-layout:fixed;width:100%;">
                    ${cols}
                    <tbody>${sections.join('')}</tbody>
                </table>
            </div>
        </div>
    </div>`;
}

// ── pane interactions (Phase 2 item 2) — installed ONCE, document-delegated ──
// The pane re-renders innerHTML on every WS caster_update repaint, so nothing is bound
// per-header: one click delegate (collapse toggle + persist) and one input delegate
// (the search ↔ collapse truce). SEARCH GOTCHA: filter-search-init.js drives row visibility
// with INLINE display on .fate-tide-sheet-ability-row, while collapse hides via the
// ft-pg-collapsed CLASS — a collapsed row matching a search would stay class-hidden. While
// the pane's search query is non-empty, ft-pa-searching on the pane root disables the
// collapse CSS entirely (search owns visibility); clearing the query restores collapse.

function paneCollapsedKeys(pane: Element): string[] {
    const keys: string[] = [];
    pane.querySelectorAll('[data-ft-pg-toggle].ft-pg-toggle--collapsed').forEach((b) => {
        const k = b.getAttribute('data-ft-pg-toggle');
        if (k) keys.push(k);
    });
    return keys;
}

function syncPassiveTreeSearchState(pane: Element): void {
    const wrap = pane.closest('.fate-tide-sheet-abilities-search-root');
    const input = wrap?.querySelector('input.fate-tide-sheet-ability-search') as HTMLInputElement | null;
    pane.classList.toggle('ft-pa-searching', !!input && input.value.trim() !== '');
}

function installPassiveTreeInteractions(): void {
    const w = window;
    if (w.__ftPassiveTreeInteractionsBound) return;
    w.__ftPassiveTreeInteractionsBound = true;

    document.addEventListener('click', (e) => {
        const btn = (e.target as HTMLElement | null)?.closest?.('[data-ft-pg-toggle]');
        if (!btn) return;
        const pane = btn.closest('.ft-pc-passive-abilities');
        if (!pane) return;
        const key = btn.getAttribute('data-ft-pg-toggle') ?? '';
        const nowCollapsed = btn.classList.toggle('ft-pg-toggle--collapsed');
        btn.setAttribute('aria-expanded', nowCollapsed ? 'false' : 'true');
        pane.querySelectorAll('tr[data-ft-pg-of]').forEach((tr) => {
            if (tr.getAttribute('data-ft-pg-of') === key) tr.classList.toggle('ft-pg-collapsed', nowCollapsed);
        });
        writeCollapsedGroupKeys(pane.getAttribute('data-ft-pg-caster') ?? '', paneCollapsedKeys(pane));
    });

    document.addEventListener('input', (e) => {
        const t = e.target as HTMLInputElement | null;
        if (!t?.classList?.contains('fate-tide-sheet-ability-search')) return;
        const wrap = t.closest('.fate-tide-sheet-abilities-search-root');
        if (!wrap) return;
        const searching = t.value.trim() !== '';
        wrap.querySelectorAll('.ft-pc-passive-abilities').forEach((p) => p.classList.toggle('ft-pa-searching', searching));
    });
}

/** Post-paint hook for the pane router: installs the document-level delegates (once) and
 *  re-syncs ft-pa-searching for the freshly-painted pane (a WS repaint can land mid-search). */
export function hydratePassiveTree(container: HTMLElement): void {
    installPassiveTreeInteractions();
    container.querySelectorAll('.ft-pc-passive-abilities').forEach(syncPassiveTreeSearchState);
}
